import { Helmet } from "react-helmet-async";
import { useSEO } from "@/hooks/use-seo";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  noIndex?: boolean;
  jsonLd?: object | object[];
}

export const SEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType,
  twitterCard,
  twitterTitle,
  twitterDescription,
  twitterImage,
  noIndex,
  jsonLd,
}: SEOProps) => {
  // Get default SEO data for current route
  const defaultSEO = useSEO();
  
  // Merge props with default SEO data, giving priority to props
  const finalTitle = title || defaultSEO.title;
  const finalDescription = description || defaultSEO.description;
  const finalKeywords = keywords || defaultSEO.keywords;
  const finalCanonicalUrl = canonicalUrl || defaultSEO.canonicalUrl;
  const finalOgTitle = ogTitle || defaultSEO.ogTitle || finalTitle;
  const finalOgDescription = ogDescription || defaultSEO.ogDescription || finalDescription;
  const finalOgImage = ogImage || defaultSEO.ogImage;
  const finalOgType = ogType || defaultSEO.ogType;
  const finalTwitterCard = twitterCard || defaultSEO.twitterCard;
  const finalTwitterTitle = twitterTitle || defaultSEO.twitterTitle || finalOgTitle;
  const finalTwitterDescription = twitterDescription || defaultSEO.twitterDescription || finalOgDescription;
  const finalTwitterImage = twitterImage || defaultSEO.twitterImage || finalOgImage;
  const finalNoIndex = noIndex ?? defaultSEO.noIndex ?? false;

  // Generate absolute URL for canonical
  const siteUrl = "https://willtheyconvert.com";
  const absoluteCanonicalUrl = finalCanonicalUrl ? (finalCanonicalUrl.startsWith('http') ? finalCanonicalUrl : `${siteUrl}${finalCanonicalUrl}`) : undefined;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      
      {/* Robots Meta Tag */}
      <meta 
        name="robots" 
        content={finalNoIndex ? "noindex, nofollow" : "index, follow"} 
      />
      
      {/* Canonical URL */}
      {absoluteCanonicalUrl && (
        <link rel="canonical" href={absoluteCanonicalUrl} />
      )}
      
      {/* Open Graph Tags */}
      <meta property="og:type" content={finalOgType} />
      {absoluteCanonicalUrl && (
        <meta property="og:url" content={absoluteCanonicalUrl} />
      )}
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={finalOgImage?.startsWith('http') ? finalOgImage : `${siteUrl}${finalOgImage}`} />
      <meta property="og:site_name" content="WillTheyConvert" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={finalTwitterCard} />
      <meta name="twitter:site" content="@willtheyconvert" />
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={finalTwitterImage?.startsWith('http') ? finalTwitterImage : `${siteUrl}${finalTwitterImage}`} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content="WillTheyConvert" />
      <meta name="publisher" content="WillTheyConvert" />

      {/* JSON-LD Structured Data */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((schema, index) => (
        <script
          key={`jsonld-${index}`}
          type="application/ld+json"
        >
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}; 